import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfileState } from '@/contexts/ProfileContext';
import { trackEvent, EVENTS } from '@/lib/analytics';

/* ================================================================================
   Overview (Dashboard) — Phase 7 Results Page
   -----------------------------------------------------------------------------
   Single-scroll narrative:
     1. Top bar (SD wordmark + "Your results")
     2. Green-gradient hero (tagline as two-tone headline, headline as body,
        translucent "Career stage" glass card, corner SD mark)
     3. Two stat badges acting as tab controls (Market demand | Target salary)
     4. Reveal panel (A: flip-card "Your next move" | B: Salary landscape bars)
     5. Honest truths (2 expand cards, equal-height headers)
     6. Blind spots (3 amber/mint cards)
   All data comes from `useProfileState().analysis` (call1 + call2) with fallbacks.
   ================================================================================ */

export default function Overview() {
  const { analysis, savedProfile } = useProfileState();
  const navigate = useNavigate();
  const c1 = analysis?.call1 || {};
  const c2 = analysis?.call2 || {};

  // Fire `dashboard_first_view` once per session so we can measure funnel
  // conversion from onboarding → dashboard land. `oncePerSession` guards
  // against duplicate fires when the user navigates between tabs.
  useEffect(() => {
    trackEvent(EVENTS.dashboard_first_view, {}, { oncePerSession: true });
  }, []);

  // Cohort-based placement: entering cohorts see the Translate-for-the-Room block
  // mid-page (right after the salary/demand reveal); in-industry cohorts see it at
  // the very end, just before the CTA banner.
  const ENTERING_COHORTS = ['enter-from-industry', 'enter-from-academia', 'recent-graduate'];
  const pivotType = savedProfile?.pivot_type || '';
  const isEntering = ENTERING_COHORTS.includes(pivotType);
  const hasTranslate = (c1.cross_sector_translation || []).length > 0;

  const [activeTab, setActiveTab] = useState('demand'); // 'demand' | 'salary'

  // Derived — badges
  const heroStat = (needle) => {
    const stats = c1.hero_stats || [];
    return stats.find(s => (s.label || '').toLowerCase().includes(needle));
  };
  const demandStat = heroStat('demand');
  const salaryStat = heroStat('salary') || heroStat('target');
  const stageStat = heroStat('stage') || heroStat('career');

  // Derived — roles
  const topRole = useMemo(() => {
    const roles = c1.roles || [];
    const expected = roles.filter(r => r.type === 'expected');
    if (expected.length) return [...expected].sort((a, b) => (b.match_pct || 0) - (a.match_pct || 0))[0];
    return roles[0];
  }, [c1]);
  const surpriseRole = useMemo(() => {
    const roles = c1.roles || [];
    return roles.find(r => r.type === 'surprise') || roles.find(r => r.type === 'adjacent');
  }, [c1]);

  // Derived — salary landscape
  const salary = useMemo(() => {
    const s = c1.salary_landscape || {};
    return {
      current: s.current_range || (c1.roles || []).find(r => r.type === 'expected')?.salary_ranges?.[0]?.range,
      next: s.next_move_range || topRole?.salary_ranges?.[0]?.range,
      stretch: s.stretch_range || surpriseRole?.salary_ranges?.[0]?.range,
      source: s.source || topRole?.salary_ranges?.[0]?.source,
      time: s.time_to_competitive_months || c1.time_to_competitive,
    };
  }, [c1, topRole, surpriseRole]);

  const rolePathsCount = (c1.roles || []).length || 6;

  const onSignOut = useCallback(() => navigate('/dashboard/roles'), [navigate]);

  return (
    <div className="sd-results -mx-4 md:-mx-10 -my-6 md:-my-10 min-h-full">
      <div className="sd-results-shell">
        {/* 1 — TOP BAR */}
        <TopBar />

        {/* 2 — HERO */}
        <HeroBanner
          tagline={c1.tagline}
          bodyHeadline={c1.headline}
        />

        {/* 3 — STAT BADGES (tabs) */}
        <div className="sd-results-badges" role="tablist" aria-label="Results insight tabs">
          <StatBadge
            id="tab-demand"
            active={activeTab === 'demand'}
            onSelect={() => setActiveTab('demand')}
            value={demandStat?.value || 'High'}
            label="Market demand"
            testId="badge-market-demand"
          />
          <StatBadge
            id="tab-salary"
            active={activeTab === 'salary'}
            onSelect={() => setActiveTab('salary')}
            value={salaryStat?.value || '$240–310K'}
            label="Target salary"
            valueClass="val--money"
            testId="badge-target-salary"
          />
        </div>

        {/* 4 — REVEAL PANEL */}
        <div className="mt-7" role="tabpanel" id={activeTab === 'demand' ? 'tab-demand-panel' : 'tab-salary-panel'}>
          {activeTab === 'demand' && (
            <div className="sd-reveal" key="demand-panel">
              <SectionLabel icon={<CompassIcon size={16} />}>Your next move</SectionLabel>
              <NextMoveFlip
                topRole={topRole}
                surpriseRole={surpriseRole}
                rolePathsCount={rolePathsCount}
                onExplore={() => navigate('/dashboard/roles')}
              />
            </div>
          )}
          {activeTab === 'salary' && (
            <div className="sd-reveal" key="salary-panel">
              <SectionLabel>Salary landscape</SectionLabel>
              <SalaryLandscape data={salary} timeText={salary.time} />
            </div>
          )}
        </div>

        {/* 5 — TRANSLATE FOR THE ROOM (mid-page for entering cohorts) */}
        {isEntering && hasTranslate && (
          <>
            <SectionLabel icon={<BridgeIcon size={16} />}>Translate for the room</SectionLabel>
            <CrossSectorTranslation items={c1.cross_sector_translation} />
          </>
        )}

        {/* 6 — HIDDEN STRENGTHS */}
        {(c2.hidden_strengths || []).length > 0 && (
          <>
            <SectionLabel icon={<GemIcon size={16} />}>Hidden strengths</SectionLabel>
            <HiddenStrengths items={c2.hidden_strengths.slice(0, 3)} />
          </>
        )}

        {/* 7 — BLIND SPOTS (expandable cards) */}
        {(c1.blind_spots || []).length > 0 && (
          <>
            <SectionLabel icon={<WarningTriangleIcon size={16} />}>Blind spots worth checking</SectionLabel>
            <div className="space-y-4">
              {c1.blind_spots.slice(0, 3).map((b, i) => (
                <BlindSpotCard key={i} b={b} idx={i} />
              ))}
            </div>
          </>
        )}

        {/* 8 — TRANSLATE FOR THE ROOM (at end for in-industry cohorts) */}
        {!isEntering && hasTranslate && (
          <>
            <SectionLabel icon={<BridgeIcon size={16} />}>Translate for the room</SectionLabel>
            <CrossSectorTranslation items={c1.cross_sector_translation} />
          </>
        )}

        {/* 9 — CTA BANNER → My Progress */}
        <ProgressCTABanner onGo={() => navigate('/dashboard/progress')} />

        <div className="h-16" />
      </div>
    </div>
  );
}

/* ============================================================
   Small SVG marks — SD logo (dark/green) and its white variant.
   Two interlocking rounded triangular halves forming an "S".
   Placeholder for the real uploaded brand asset.
   ============================================================ */

function SDMark({ size = 30, variant = 'dark', padded = false }) {
  // Official Sustainability Decoded mark — two interlocking teardrop halves.
  // Path geometry is identical across variants; only fills differ.
  //   variant='dark'  → black upper + brand-green lower  (for light backgrounds, e.g. top bar)
  //   variant='white' → both halves white                 (for green backgrounds, e.g. hero corner)
  //   variant='green' → both halves brand-green           (accent-only usage)
  //
  // Layout:
  //   padded=false → tight portrait viewBox (0.58:1). Best for inline pairings
  //                  with wordmark text — no wasted horizontal space.
  //   padded=true  → square viewBox with breathing room around the artwork.
  //                  Best for standalone brand placement (e.g. hero corner) so
  //                  the mark reads as a proper logo rather than a shape
  //                  touching an edge.
  const upper = variant === 'white' ? '#FFFFFF' : variant === 'green' ? '#44C349' : '#010101';
  const lower = variant === 'white' ? '#FFFFFF' : '#44C349';
  // Tight portrait viewBox: mark occupies the full render box (~0.58:1 aspect).
  // Square padded viewBox: adds ~110px horizontal + 20px vertical padding
  // around the artwork so the mark centers in an equal-sided box.
  const viewBox = padded ? '280 280 440 440' : '378 290 244 420';
  const svgProps = padded
    ? { width: size, height: size }
    : { height: size };
  return (
    <svg
      {...svgProps}
      viewBox={viewBox}
      fill="none"
      aria-label="Sustainability Decoded"
      role="img"
    >
      <path
        d="M452.9,300a128.6,128.6,0,0,0-42.7,249.86,11.19,11.19,0,0,0,11.62-2.71L611.21,357.77c21.32-21.32,6.22-57.77-23.93-57.77Zm102.75,51.25-31.53,31.51-117,117A84.67,84.67,0,0,1,452.9,343.9H563Z"
        fill={upper}
      />
      <path
        d="M547.1,700a128.6,128.6,0,0,0,42.7-249.86,11.19,11.19,0,0,0-11.62,2.71L388.79,642.23C367.47,663.55,382.57,700,412.72,700ZM444.35,648.75l31.53-31.51,117-117A84.67,84.67,0,0,1,547.1,656.1H437Z"
        fill={lower}
      />
    </svg>
  );
}

/* ============================================================
   1) Top bar
   ============================================================ */

function TopBar() {
  const onExport = useCallback(() => {
    // Open the full-profile PDF report in a new tab. It auto-triggers window.print()
    // once the analysis data has loaded so users can Save as PDF.
    try {
      window.open('/dashboard/report?print=1', '_blank', 'noopener');
    } catch (e) { /* older browsers */ }
  }, []);
  return (
    <header className="sd-results-topbar" data-testid="results-topbar">
      <SDMark size={38} variant="dark" padded />
      <div className="flex items-center gap-2 leading-none">
        <span className="text-[16px] font-extrabold tracking-tight text-[color:var(--r-ink)]">SD·Pivot</span>
        <span className="text-[12px] font-medium text-[color:var(--r-muted)] pl-2 border-l border-[color:var(--r-hairline)]">
          Your results
        </span>
      </div>
      <div className="flex-1" />
      <button
        type="button"
        aria-label="Export your full career report as PDF"
        title="Export your career report. Each JD analysis can be exported individually from its results page."
        onClick={onExport}
        className="sd-export-btn"
        data-testid="results-export"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
          <polyline points="16 6 12 2 8 6" />
          <line x1="12" y1="2" x2="12" y2="15" />
        </svg>
        <span className="sd-export-lbl">Export</span>
      </button>
    </header>
  );
}

/* ============================================================
   2) Hero banner
   ============================================================ */

function splitHighlight(text) {
  // Split a headline into (pre + highlight) so the tail gets a rounded highlight.
  const s = String(text || '').trim();
  if (!s) return { pre: '', hi: '' };
  // Prefer the phrase after "is your", "is a", "is an", "is the"
  const anchors = [/\bis your\b/i, /\bis a\b/i, /\bis an\b/i, /\bis the\b/i];
  for (const a of anchors) {
    const m = s.match(a);
    if (m) {
      const start = s.indexOf(m[0]) + m[0].length + 1;
      const tail = s.slice(start).trim();
      if (tail && tail.length < s.length * 0.72) {
        return { pre: s.slice(0, start).trim() + ' ', hi: tail };
      }
    }
  }
  // Fallback: last 2-3 words
  const words = s.split(/\s+/);
  if (words.length <= 4) return { pre: '', hi: s };
  const take = words.length >= 6 ? 3 : 2;
  return { pre: words.slice(0, -take).join(' ') + ' ', hi: words.slice(-take).join(' ') };
}

function HeroBanner({ tagline, bodyHeadline }) {
  const { pre, hi } = splitHighlight(tagline || 'Your sustainability career is worth more than you think.');
  return (
    <section
      className="sd-results-hero mt-6"
      data-testid="results-hero"
    >
      {/* Faint corner logo */}
      <div className="sd-results-hero-logo hidden sm:block" aria-hidden>
        <SDMark size={164} variant="white" padded />
      </div>

      {/* Small pill */}
      <div className="sd-results-hero-pill" data-testid="hero-pill">
        <span className="dot" aria-hidden />
        Your career position
      </div>

      {/* Two-tone headline */}
      <h1 className="sd-results-hero-headline" data-testid="hero-headline">
        {pre}
        <span className="sd-results-headline-highlight">{hi}</span>
      </h1>

      {/* Body paragraph */}
      {bodyHeadline && (
        <p className="sd-results-hero-body" data-testid="hero-body">{bodyHeadline}</p>
      )}
    </section>
  );
}

/* ============================================================
   3) Stat badge (also acts as tab control)
   ============================================================ */

function StatBadge({ id, active, onSelect, value, label, valueClass = '', testId }) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-selected={active}
      aria-controls={`${id}-panel`}
      onClick={onSelect}
      className={`sd-results-badge ${active ? 'sd-results-badge--active' : ''}`}
      data-testid={testId}
    >
      <div className={`val ${valueClass}`}>{value}</div>
      <div className="lbl">
        {label}
        <svg
          width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: active ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 240ms cubic-bezier(.2,.8,.2,1)', marginLeft: 2 }}
          aria-hidden
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
    </button>
  );
}

/* ============================================================
   Section label (deep-green with short green bar)
   ============================================================ */

function SectionLabel({ children, icon }) {
  return (
    <div className={`sd-results-section-label ${icon ? 'sd-results-section-label--with-icon' : ''}`}>
      {icon && <span className="sd-results-section-icon">{icon}</span>}
      {children}
    </div>
  );
}

/* ============================================================
   4A) Reveal panel A — "Your next move" flip card
   ============================================================ */

function ArrowCircleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
function FlipIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
}
function GemIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 3h12l3 6-9 12L3 9z" />
      <path d="M12 3L9 9l3 12 3-12-3-6z" />
      <path d="M3 9h18" />
    </svg>
  );
}

function CompassIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" stroke="none" />
    </svg>
  );
}

function WarningTriangleIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3 L22 20 L2 20 Z" />
      <circle cx="12" cy="14.5" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

function BridgeIcon({ size = 16 }) {
  // Two arches with a horizontal line — reads as a bridge between two shores
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 17 L22 17" />
      <path d="M4 17 V11 A4 4 0 0 1 12 11 V17" />
      <path d="M12 17 V11 A4 4 0 0 1 20 11 V17" />
      <path d="M4 20 V17" />
      <path d="M20 20 V17" />
    </svg>
  );
}

function MatchRing({ pct, size = 108, dark = false }) {
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const target = Math.max(0, Math.min(100, pct || 0));
  const [drawn, setDrawn] = useState(0);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setDrawn(target); return;
    }
    let raf;
    const t0 = performance.now();
    const from = drawn;
    const dur = 900;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDrawn(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const trackColor = dark ? 'rgba(255,255,255,.12)' : '#E7ECEA';
  const grad = dark ? 'url(#matchRingDark)' : 'url(#matchRingLight)';
  const offset = c - (c * drawn) / 100;
  return (
    <div className="sd-match-ring">
      <svg width={size} height={size}>
        <defs>
          <linearGradient id="matchRingLight" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#4CD964" />
            <stop offset="100%" stopColor="#18B771" />
          </linearGradient>
          <linearGradient id="matchRingDark" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#45D97E" />
            <stop offset="100%" stopColor="#15A76D" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} stroke={trackColor} strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={grad} strokeWidth={stroke} fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="val" style={{ color: dark ? '#fff' : '#0F1A14' }}>
        <div className="n">{drawn}%</div>
        <div className="l">Match</div>
      </div>
    </div>
  );
}

function extractLastPhrase(title) {
  // "Vice President, Sustainability — Americas" → { main, tail: "Americas" }
  const s = String(title || '').trim();
  const emDashSplit = s.split(/\s*[—–-]\s*/);
  if (emDashSplit.length >= 2) {
    return { main: emDashSplit.slice(0, -1).join(' — '), tail: emDashSplit[emDashSplit.length - 1] };
  }
  const comma = s.lastIndexOf(',');
  if (comma > 0 && s.length - comma < 32) {
    return { main: s.slice(0, comma).trim(), tail: s.slice(comma + 1).trim() };
  }
  return { main: s, tail: '' };
}

function NextMoveFlip({ topRole, surpriseRole, rolePathsCount, onExplore }) {
  const [flipped, setFlipped] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [minH, setMinH] = useState(360);

  const measure = useCallback(() => {
    const fh = frontRef.current?.scrollHeight || 0;
    const bh = backRef.current?.scrollHeight || 0;
    const h = Math.max(fh, bh, 340);
    setMinH(h);
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, topRole, surpriseRole]);

  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    // Re-measure after fonts have loaded (webfont may shift heights)
    if (document?.fonts?.ready) {
      document.fonts.ready.then(() => measure()).catch(() => {});
    }
    // Extra deferred measure to catch late layout (e.g., match ring animation)
    const t = setTimeout(measure, 250);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, [measure]);

  const stop = (e) => { e.stopPropagation(); };
  const doExplore = (e) => { e.stopPropagation(); onExplore(); };

  if (!topRole && !surpriseRole) {
    return (
      <div className="sd-results-card p-8 text-[color:var(--r-muted)] text-[14px]">
        Role details are being finalised. Refresh in a moment.
      </div>
    );
  }

  const topSalary = topRole?.salary_ranges?.[0];
  const surSalary = surpriseRole?.salary_ranges?.[0];
  const topSplit = extractLastPhrase(topRole?.title);
  const surSplit = extractLastPhrase(surpriseRole?.title);

  return (
    <div
      className={`sd-flip ${flipped ? 'is-flipped' : ''}`}
      style={{ minHeight: minH }}
      onClick={() => setFlipped(f => !f)}
      role="button"
      tabIndex={0}
      aria-pressed={flipped}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFlipped(f => !f); } }}
      data-testid="next-move-flip"
    >
      <div className="sd-flip-inner" style={{ minHeight: minH }}>
        {/* FRONT — strongest match */}
        <div className="sd-flip-face sd-flip-face--front" ref={frontRef}>
          <div className="p-7 md:p-8 pl-9 md:pl-10 flex flex-col gap-5" style={{ minHeight: minH }}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[color:var(--r-mint)] text-[color:var(--r-green-deep)] text-[11.5px] font-bold uppercase tracking-wider">
                Strongest match
              </span>
              <button
                type="button"
                className="sd-arrow-btn sd-arrow-btn--green !text-[12.5px] !py-2 !pr-2 !pl-4"
                onClick={(e) => { stop(e); setFlipped(true); }}
                data-testid="flip-to-surprise"
              >
                Flip this card
                <span className="arrow-circle" style={{ width: 24, height: 24 }}>
                  <FlipIcon />
                </span>
              </button>
            </div>

            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <h2 className="text-[26px] md:text-[30px] font-extrabold leading-[1.15] tracking-tight text-[color:var(--r-ink)]" data-testid="next-move-front-title">
                  {topSplit.main}
                  {topSplit.tail && (
                    <>
                      <span className="text-[color:var(--r-green-deep)]"> — {topSplit.tail}</span>
                    </>
                  )}
                </h2>
                <p className="mt-3 text-[14.5px] leading-relaxed text-[color:var(--r-ink-2)]">
                  {topRole?.why || 'You are already operating at this scope — the title upgrade is the most direct next step.'}
                </p>
                {topSalary?.range && (
                  <div className="mt-4 inline-flex items-baseline gap-2 px-3.5 py-2 rounded-full bg-[color:var(--r-mint)]">
                    <span className="font-mono text-[15px] font-bold text-[color:var(--r-green-deep)]">{topSalary.range}</span>
                    <span className="text-[11px] text-[color:var(--r-muted)]">
                      {topSalary.geo}{topSalary.source ? ` · ${topSalary.source}` : ''}
                    </span>
                  </div>
                )}
              </div>

              {typeof topRole?.match_pct === 'number' && (
                <MatchRing pct={topRole.match_pct} size={112} />
              )}
            </div>

            <div className="mt-auto flex items-center justify-between gap-3 flex-wrap pt-2">
              <button
                type="button"
                className="sd-arrow-btn sd-arrow-btn--green"
                onClick={doExplore}
                data-testid="next-move-front-cta"
              >
                See in Role Explorer
                <span className="arrow-circle"><ArrowCircleIcon /></span>
              </button>
              <span className="text-[12.5px] text-[color:var(--r-muted)]">
                {rolePathsCount} role path{rolePathsCount === 1 ? '' : 's'} match your profile.
              </span>
            </div>
          </div>
        </div>

        {/* BACK — surprise role */}
        <div className="sd-flip-face sd-flip-face--back" ref={backRef}>
          <div className="p-7 md:p-8 flex flex-col gap-5 text-white" style={{ minHeight: minH }}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider"
                    style={{ background: 'rgba(76,217,100,.20)', color: '#C7FFDA', border: '1px solid rgba(199,255,218,.30)' }}>
                A role you hadn't considered
              </span>
              <button
                type="button"
                className="sd-arrow-btn sd-arrow-btn--white !text-[12.5px] !py-2 !pr-2 !pl-4"
                onClick={(e) => { stop(e); setFlipped(false); }}
                data-testid="flip-to-match"
              >
                Back to your match
                <span className="arrow-circle" style={{ width: 24, height: 24 }}>
                  <FlipIcon />
                </span>
              </button>
            </div>

            <div className="flex items-center justify-between gap-6 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <h2 className="text-[26px] md:text-[30px] font-extrabold leading-[1.15] tracking-tight text-white" data-testid="next-move-back-title">
                  {surSplit.main || 'A stretch you should consider'}
                  {surSplit.tail && (
                    <>
                      <span style={{ color: '#4CD964' }}> — {surSplit.tail}</span>
                    </>
                  )}
                </h2>
                <p className="mt-3 text-[14.5px] leading-relaxed" style={{ color: 'rgba(238, 255, 245, .78)' }}>
                  {surpriseRole?.why || 'A less-obvious path that leverages exactly the assets your profile already has.'}
                </p>
                {surSalary?.range && (
                  <div className="mt-4 inline-flex items-baseline gap-2 px-3.5 py-2 rounded-full"
                       style={{ background: 'rgba(76, 217, 100, .14)', border: '1px solid rgba(76, 217, 100, .28)' }}>
                    <span className="font-mono text-[15px] font-bold" style={{ color: '#4CD964' }}>{surSalary.range}</span>
                    <span className="text-[11px]" style={{ color: 'rgba(199, 255, 218, .72)' }}>
                      {surSalary.geo}{surSalary.source ? ` · ${surSalary.source}` : ''}
                    </span>
                  </div>
                )}
              </div>

              {typeof surpriseRole?.match_pct === 'number' && (
                <MatchRing pct={surpriseRole.match_pct} size={112} dark />
              )}
            </div>

            <div className="mt-auto flex items-center justify-between gap-3 flex-wrap pt-2">
              <button
                type="button"
                className="sd-arrow-btn sd-arrow-btn--white"
                onClick={doExplore}
                data-testid="next-move-back-cta"
              >
                See in Role Explorer
                <span className="arrow-circle"><ArrowCircleIcon /></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   4B) Reveal panel B — Salary landscape
   ============================================================ */

/* ============================================================
   Parse the max numeric value from a salary range string.
   ============================================================
   Salary ranges arrive in many currency conventions across markets:
     "$180K–$250K"        USA / global — thousands
     "£95K"               UK
     "€90k"               EU
     "MX$720K–MX$1.1M"    Mexico — mixes K and M
     "R$85k–R$140k"       Brazil
     "¥12M–¥18M"          Japan — millions of yen
     "₹40L–₹90L"          India — lakhs (1L = 100,000)
     "₹1.2Cr–₹2.5Cr"      India — crores (1Cr = 10,000,000)
     "S$150K–S$200K"      Singapore
     "Rp 500jt–Rp 1.2M"   Indonesia — juta = million
     "$1.2B"              (unlikely but honor the scale)
   The old regex only accepted `[Kk]?` — it missed M/B/L/Cr, which made the
   Stretch bar (usually the one that jumps into "M") register at the WRONG
   magnitude and appear narrower than the Next-move bar. Result: users saw
   the Stretch column disproportionately shorter than expected across every
   market that hits a scale-suffix boundary.

   This parser scans for a NUMBER followed by an optional scale suffix and
   returns the highest resulting value. Case-insensitive; whitespace tolerant.
*/
const _SCALE_MULTIPLIERS = {
  K: 1_000,
  M: 1_000_000,
  B: 1_000_000_000,
  L: 100_000,          // Indian "lakh"
  CR: 10_000_000,      // Indian "crore"
  JT: 1_000_000,       // Indonesian "juta"
  MN: 1_000_000,       // "mn" abbreviation
  BN: 1_000_000_000,   // "bn" abbreviation
};

function extractMax(range) {
  if (!range) return 0;
  const s = String(range);
  // Match a number, optional decimal, optional whitespace, optional suffix
  // (K / M / B / L / Cr / Jt / Mn / Bn). Suffix is case-insensitive.
  const re = /(\d+(?:\.\d+)?)\s*(cr|jt|mn|bn|k|m|b|l)?/gi;
  let best = 0;
  let match;
  while ((match = re.exec(s)) !== null) {
    const n = parseFloat(match[1]);
    if (!Number.isFinite(n)) continue;
    const suffix = (match[2] || '').toUpperCase();
    const mult = _SCALE_MULTIPLIERS[suffix] || 1;
    const val = n * mult;
    if (val > best) best = val;
  }
  return best;
}

function SalaryLandscape({ data, timeText }) {
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    // trigger the bar scale animation on mount (whenever the tab is opened)
    const t = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const rows = [
    { key: 'current', label: 'Current', range: data.current, cls: 'sd-salary-bar--current', pct: 60 },
    { key: 'next',    label: 'Next move', range: data.next, cls: 'sd-salary-bar--next', pct: 76 },
    { key: 'stretch', label: 'Stretch', range: data.stretch, cls: 'sd-salary-bar--stretch', pct: 100 },
  ].filter(r => r.range);

  // Compute proportional widths if all three have numeric ranges; else fallback to spec percentages
  const maxes = rows.map(r => extractMax(r.range));
  const chartMax = Math.max(...maxes, 1);
  const useProportional = maxes.every(m => m > 0) && rows.length >= 2;

  return (
    <div className="sd-results-card p-7 md:p-8" data-testid="salary-landscape">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <h2 className="text-[24px] md:text-[26px] font-extrabold tracking-tight text-[color:var(--r-ink)]">
          Current, target, stretch
        </h2>
        {timeText && (
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[color:var(--r-mint)] text-[color:var(--r-green-deep)] text-[12px] font-bold">
            Competitive in {timeText.toLowerCase().includes('month') ? timeText : `${timeText} months`}
          </span>
        )}
      </div>
      <p className="text-[13.5px] text-[color:var(--r-ink-2)] leading-relaxed max-w-[62ch] mb-6">
        Your current market rate, your next-move target, and the stretch move that puts you in a different league.
      </p>

      <div className="sd-salary-chart">
        {rows.map((r, i) => {
          const pctWidth = useProportional
            ? Math.max(28, Math.round((extractMax(r.range) / chartMax) * 100))
            : r.pct;
          return (
            <React.Fragment key={r.key}>
              <div
                className={`sd-salary-bar ${r.cls}`}
                style={{
                  width: `${pctWidth}%`,
                  transform: animated ? 'scaleX(1)' : 'scaleX(0.32)',
                  transitionDelay: `${i * 110}ms`,
                }}
                data-testid={`salary-row-${r.key}`}
              >
                {r.label}
              </div>
              <span className="sd-salary-amt">{r.range}</span>
            </React.Fragment>
          );
        })}
      </div>

      {data.source && (
        <div className="mt-5 text-[11.5px] font-medium text-[color:var(--r-muted)] uppercase tracking-wider">
          Source · {data.source}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   5) "Translate for the room" — Triple-swap 3D flip cards.
      Each card cycles on tap through three faces of the SAME
      experience for three audiences:
        Face 0 — Your experience  (light card, green edge, dark text)
        Face 1 — Speak sustainability  (green gradient, white text)
        Face 2 — Speak business  (charcoal-green, white text)
      3D flip animation on each tap; content of the incoming face
      is set BEFORE the flip so the new room appears already rendered.
   ============================================================ */

const T3_FACES = [
  { key: 'exp',  label: 'Your experience',      cls: 'sd-t3--exp'  },
  { key: 'sust', label: 'Speak sustainability', cls: 'sd-t3--sust' },
  { key: 'biz',  label: 'Speak business',       cls: 'sd-t3--biz'  },
];

function CrossSectorTranslation({ items }) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="sd-t3-grid" data-testid="cross-sector-translation">
      {list.map((item, i) => (
        <CrossSectorCard key={i} item={item} idx={i} />
      ))}
    </div>
  );
}

function CrossSectorCard({ item, idx }) {
  // Normalize content — supports new triple-face schema and legacy shape.
  // ALWAYS render three faces so the mechanic is a true triple-swap. If a face is
  // missing from a legacy analysis, show a clear "refresh" placeholder so the user
  // knows a room exists rather than silently hiding it.
  const faces = useMemo(() => {
    const exp = (item.user_experience || item.background || '').trim();
    const sust = (item.speak_sustainability || item.sustainability_equivalent || item.translation || '').trim();
    const biz = (item.speak_business || '').trim();
    const REFRESH = 'Refresh your analysis (New Assessment) to see this view.';
    return [
      exp || REFRESH,
      sust || REFRESH,
      biz || REFRESH,
    ];
  }, [item]);

  const N = 3; // Always three rooms

  // `stage` = which face the user is currently seeing (0, 1, 2).
  // `spinCount` = total taps (drives rotation and which physical side is visible).
  const [stage, setStage] = useState(0);
  const [spinCount, setSpinCount] = useState(0);

  const nextStage = (stage + 1) % N;
  const currentIdx = stage;
  const nextIdx = nextStage;

  // Pre-load: the VISIBLE face shows currentIdx, the HIDDEN face shows nextIdx.
  // Even spin => front is visible. Odd spin => back is visible.
  const isFrontVisible = spinCount % 2 === 0;
  const frontFaceIdx = isFrontVisible ? currentIdx : nextIdx;
  const backFaceIdx  = isFrontVisible ? nextIdx : currentIdx;

  const rotationDeg = spinCount * 180;

  const onFlip = useCallback(() => {
    setSpinCount((s) => s + 1);
    setStage((s) => (s + 1) % N);
  }, []);

  return (
    <div
      className="sd-t3-card"
      data-testid={`cross-sector-card-${idx}`}
      role="button"
      tabIndex={0}
      onClick={onFlip}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onFlip();
        }
      }}
      aria-label="Flip card to translate this experience for a different room (Your experience, Speak sustainability, Speak business)"
    >
      <div
        className="sd-t3-inner"
        style={{ transform: `rotateY(${rotationDeg}deg)` }}
      >
        <TripleFace side="front" faceIdx={frontFaceIdx} text={faces[frontFaceIdx]} stage={stage} dotsCount={N} />
        <TripleFace side="back"  faceIdx={backFaceIdx}  text={faces[backFaceIdx]}  stage={stage} dotsCount={N} />
      </div>
    </div>
  );
}

function TripleFace({ side, faceIdx, text, stage, dotsCount }) {
  const face = T3_FACES[faceIdx] || T3_FACES[0];
  return (
    <div className={`sd-t3-face sd-t3-face--${side} ${face.cls}`} data-testid={`t3-face-${face.key}`}>
      <div className="sd-t3-face-top">
        <div className="sd-t3-label">{face.label}</div>
        <span className="sd-t3-flipicon" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12a8 8 0 0 1 14-5.3" />
            <polyline points="18 3 18 7 14 7" />
            <path d="M20 12a8 8 0 0 1-14 5.3" />
            <polyline points="6 21 6 17 10 17" />
          </svg>
        </span>
      </div>
      <div className="sd-t3-copy">{text}</div>
      <div className="sd-t3-dots" role="presentation">
        {Array.from({ length: dotsCount }).map((_, i) => (
          <span
            key={i}
            className={`sd-t3-dot ${i === stage ? 'is-active' : ''}`}
          />
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   6) Hidden Strengths — 3 stacked expand cards, equal-height headers
      (previously "Honest Truths"). Each card surfaces one under-recognised
      strength: short punchy headline in the green header, reasoning +
      optional "how to use" revealed on expand.
   ============================================================ */

function HiddenStrengths({ items }) {
  const list = (items || []).slice(0, 3).filter(Boolean);

  const headRefs = useRef([]);
  const [minHead, setMinHead] = useState(null);

  const measureHeads = useCallback(() => {
    const heights = headRefs.current.map((el) => (el ? el.getBoundingClientRect().height : 0));
    const max = Math.max(...heights, 0);
    if (max > 0) setMinHead(max);
  }, []);

  useLayoutEffect(() => {
    setMinHead(null);
    const raf = requestAnimationFrame(() => measureHeads());
    return () => cancelAnimationFrame(raf);
  }, [list.length, measureHeads]);

  useEffect(() => {
    const onResize = () => {
      setMinHead(null);
      requestAnimationFrame(() => measureHeads());
    };
    window.addEventListener('resize', onResize);
    if (document?.fonts?.ready) {
      document.fonts.ready.then(() => measureHeads()).catch(() => {});
    }
    const t = setTimeout(measureHeads, 200);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, [measureHeads]);

  // 3 items → 3-col on desktop, 1-col on mobile.
  const cols = list.length >= 3 ? 'md:grid-cols-3' : list.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1';

  return (
    <div className={`grid gap-5 items-start ${cols}`}>
      {list.map((s, i) => (
        <HiddenStrengthCard
          key={i}
          idx={i}
          strength={s}
          headRef={(el) => { headRefs.current[i] = el; }}
          headMinH={minHead}
        />
      ))}
    </div>
  );
}

function HiddenStrengthCard({ idx, strength, headRef, headMinH }) {
  const [open, setOpen] = useState(false);
  const headline = strength?.headline || '';
  const reasoning = strength?.reasoning || '';
  const howToUse = strength?.how_to_use || '';
  const hasDetail = Boolean(reasoning || howToUse);

  return (
    <article className="sd-truth-card" data-testid={`hidden-strength-${idx}`}>
      <button
        ref={headRef}
        type="button"
        className={`sd-truth-head ${open ? 'is-open' : ''}`}
        onClick={() => hasDetail && setOpen(o => !o)}
        aria-expanded={open}
        aria-controls={`strength-panel-${idx}`}
        style={{ minHeight: headMinH || undefined, cursor: hasDetail ? 'pointer' : 'default' }}
        data-testid={`hidden-strength-head-${idx}`}
      >
        <div className="sd-truth-head-row">
          {hasDetail ? (
            <span className="sd-truth-chevron" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          ) : <span aria-hidden />}
        </div>
        <div className="sd-truth-insight">{headline}</div>
      </button>

      {hasDetail && (
        <div
          id={`strength-panel-${idx}`}
          role="region"
          aria-labelledby={`hidden-strength-head-${idx}`}
          className={`sd-truth-panel ${open ? 'is-open' : ''}`}
        >
          {reasoning && <p className="sd-truth-reason">{reasoning}</p>}
          {howToUse && (
            <div className="sd-truth-howto">
              <div className="sd-truth-howto-lbl">How to use it</div>
              <p>{howToUse}</p>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

/* ============================================================
   6) Blind spot card (amber signal, mint move box)
   ============================================================ */

function deriveMove(text) {
  const s = String(text || '').trim();
  if (!s) return { analysis: '', move: '' };

  // 1) Prefer em-dash / en-dash split — the natural context → recommendation pivot.
  const dashMatch = s.match(/\s+[—–]\s+/);
  if (dashMatch) {
    const idx = s.indexOf(dashMatch[0]);
    if (idx > 30 && s.length - idx > 30) {
      let analysis = s.slice(0, idx).trim();
      if (!/[.!?]$/.test(analysis)) analysis += '.';
      let move = s.slice(idx + dashMatch[0].length).trim();
      // Strip common transitional openers so "move" reads as a clear directive.
      move = move.replace(/^(?:instead[,]?\s+|but you need to\s+|but you\s+|and\s+)/i, '');
      // Ensure move ends with terminal punctuation.
      if (!/[.!?]$/.test(move)) move += '.';
      // Capitalize first letter.
      move = move.charAt(0).toUpperCase() + move.slice(1);
      return { analysis, move };
    }
  }

  // 2) Fallback — last sentence is the recommendation.
  const sentences = s.match(/[^.!?]+[.!?]+/g);
  if (sentences && sentences.length > 1) {
    return {
      analysis: sentences.slice(0, -1).join(' ').trim(),
      move: sentences[sentences.length - 1].trim(),
    };
  }

  // 3) No natural split — treat the entire text as analysis (no separate move box).
  return { analysis: s, move: '' };
}

function BlindSpotCard({ b, idx }) {
  const { analysis, move } = deriveMove(b?.explanation || '');
  const actualMove = b?.action || move;
  const [open, setOpen] = useState(false);
  const hasBody = Boolean(analysis) || Boolean(actualMove);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  return (
    <article
      className={`sd-blindspot-card ${open ? 'is-open' : ''}`}
      data-testid={`blind-spot-${idx}`}
    >
      <button
        type="button"
        className="sd-blindspot-head"
        onClick={hasBody ? toggle : undefined}
        aria-expanded={open}
        aria-controls={`blind-spot-panel-${idx}`}
        data-testid={`blind-spot-toggle-${idx}`}
      >
        <div className="sd-blindspot-head-left">
          <div className="sd-blindspot-header">
            <span className="sd-blindspot-dot" aria-hidden />
            <span className="sd-blindspot-label">Blind spot</span>
          </div>
          <h3 className="sd-blindspot-title">{b?.title}</h3>
        </div>
        {hasBody && (
          <span className={`sd-blindspot-chevron ${open ? 'is-open' : ''}`} aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        )}
      </button>
      {hasBody && (
        <div
          id={`blind-spot-panel-${idx}`}
          className={`sd-blindspot-panel ${open ? 'is-open' : ''}`}
          role="region"
          aria-hidden={!open}
        >
          <div className="sd-blindspot-panel-inner">
            {analysis && <p className="sd-blindspot-analysis">{analysis}</p>}
            {actualMove && (
              <div className="sd-blindspot-move">
                <div className="lbl">The move</div>
                <div className="txt">{actualMove}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

/* ============================================================
   9) Progress CTA banner — end-of-analysis call to action.
      Full-width green gradient banner that takes users to My Progress.
   ============================================================ */
function ProgressCTABanner({ onGo }) {
  return (
    <section className="sd-cta-banner" data-testid="progress-cta-banner">
      <div className="sd-cta-banner-body">
        <h2 className="sd-cta-banner-title">Turn insight into action.</h2>
        <p className="sd-cta-banner-sub">
          Your milestones, next-90-day roadmap, and every commit you have made so far live in My Progress. Track what actually moves.
        </p>
      </div>
      <button
        type="button"
        onClick={onGo}
        className="sd-cta-banner-btn"
        data-testid="progress-cta-btn"
      >
        Go to My Progress
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
    </section>
  );
}
